export interface TherapistSettingsStatusInput {
  approved: boolean;
  deactivated: boolean;
  onboardingCompleted: boolean;
  availabilityCount: number;
  activeServiceCount: number;
  profile: {
    hasName: boolean;
    hasBio: boolean;
    hasSpecializations: boolean;
    hasPhoto: boolean;
    hasPhone: boolean;
  };
  billingReady: boolean;
  matchingReady: boolean;
  emergencyEnabled: boolean;
  googleConnected: boolean;
}

export type SettingsStatusTone = "ready" | "warning" | "blocked" | "neutral";

export interface SettingsStatusResult {
  reservations: {
    ready: boolean;
    tone: SettingsStatusTone;
    label: string;
    detail: string;
    blockers: string[];
  };
  publicProfile: {
    ready: boolean;
    tone: SettingsStatusTone;
    label: string;
    detail: string;
    missing: string[];
  };
  administration: {
    ready: boolean;
    tone: SettingsStatusTone;
    label: string;
    detail: string;
  };
  optional: {
    matchingReady: boolean;
    emergencyEnabled: boolean;
    googleConnected: boolean;
  };
}

export function computeTherapistSettingsStatus(
  input: TherapistSettingsStatusInput,
): SettingsStatusResult {
  const blockers: string[] = [];
  if (!input.approved) blockers.push("Profil nije odobren");
  if (input.deactivated) blockers.push("Profil je deaktiviran");
  if (!input.onboardingCompleted) blockers.push("Onboarding nije završen");
  if (input.availabilityCount < 1) blockers.push("Nema radnog vremena");
  if (input.activeServiceCount < 1) blockers.push("Nema aktivnih usluga");

  const reservationsReady = blockers.length === 0;

  const missing: string[] = [];
  if (!input.profile.hasName) missing.push("ime i prezime");
  if (!input.profile.hasBio) missing.push("bio");
  if (!input.profile.hasSpecializations) missing.push("specijalizacije");
  if (!input.profile.hasPhoto) missing.push("fotografija");
  if (!input.profile.hasPhone) missing.push("telefon");

  const publicProfileReady = missing.length === 0;

  return {
    reservations: {
      ready: reservationsReady,
      tone: reservationsReady ? "ready" : input.approved && !input.deactivated ? "warning" : "blocked",
      label: reservationsReady ? "Rezervacije aktivne" : "Rezervacije nisu spremne",
      detail: reservationsReady
        ? "Klijenti mogu rezervisati dostupne termine."
        : blockers[0] ?? "Provjeri postavke rezervacija.",
      blockers,
    },
    publicProfile: {
      ready: publicProfileReady,
      tone: publicProfileReady ? "ready" : "warning",
      label: publicProfileReady ? "Javni profil spreman" : "Javni profil treba doradu",
      detail: publicProfileReady
        ? "Osnovni javni podaci su popunjeni."
        : `Nedostaje: ${missing.slice(0, 3).join(", ")}${missing.length > 3 ? "..." : ""}`,
      missing,
    },
    administration: {
      ready: input.billingReady,
      tone: input.billingReady ? "ready" : "warning",
      label: input.billingReady ? "Naplata spremna" : "Naplata nije kompletna",
      detail: input.billingReady
        ? "Podaci za račune i isplate su popunjeni."
        : "Dopuni podatke za naplatu prije administracije isplata.",
    },
    optional: {
      matchingReady: input.matchingReady,
      emergencyEnabled: input.emergencyEnabled,
      googleConnected: input.googleConnected,
    },
  };
}
